import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Users, Home, Pill, CalendarDays, AlertOctagon, ClipboardList,
  Activity,
} from "lucide-react";
import api from "@/lib/api";

// Tone → tailwind classes for the header pill + left rail
const TONES = {
  green: {
    rail: "before:bg-emerald-500",
    pill: "bg-emerald-100 text-emerald-800 border-emerald-200",
    ring: "hover:border-emerald-400",
    dot: "bg-emerald-500",
  },
  amber: {
    rail: "before:bg-amber-500",
    pill: "bg-amber-100 text-amber-900 border-amber-300",
    ring: "hover:border-amber-400",
    dot: "bg-amber-500",
  },
  red: {
    rail: "before:bg-red-600",
    pill: "bg-red-100 text-red-800 border-red-300",
    ring: "hover:border-red-400",
    dot: "bg-red-600",
  },
};

function Card({ to, icon: Icon, label, headline, sub, status = "green", testid }) {
  const tone = TONES[status] || TONES.green;
  return (
    <Link
      to={to}
      data-testid={testid}
      className={`relative pl-3 pr-4 py-3 bg-white border border-slate-200 rounded-lg shadow-sm transition-colors before:absolute before:left-0 before:top-0 before:bottom-0 before:w-1 before:rounded-l-lg ${tone.rail} ${tone.ring} focus:outline-none focus:ring-2 focus:ring-sky-400 flex flex-col gap-1.5 min-h-[112px]`}
    >
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.14em] font-semibold text-slate-500">
          <Icon className="w-3.5 h-3.5 text-slate-500" aria-hidden="true" />
          <span className="truncate">{label}</span>
        </div>
        <span className={`inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded-full border ${tone.pill}`}>
          <span className={`w-1.5 h-1.5 rounded-full ${tone.dot}`} aria-hidden="true" />
          {status}
        </span>
      </div>
      <div className="font-heading font-semibold text-slate-900 text-xl leading-tight tabular-nums">
        {headline}
      </div>
      <div className="text-[12px] text-slate-600 leading-snug space-y-0.5 min-h-[32px]">
        {sub}
      </div>
    </Link>
  );
}

export default function TodaysOperationalSummary() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    api.get("/dashboard/ops-summary")
      .then((r) => { if (alive) setData(r.data); })
      .catch(() => { if (alive) setData(null); })
      .finally(() => { if (alive) setLoading(false); });
    const t = setInterval(() => {
      api.get("/dashboard/ops-summary").then((r) => { if (alive) setData(r.data); }).catch(() => {});
    }, 60_000);
    return () => { alive = false; clearInterval(t); };
  }, []);

  if (loading && !data) {
    return (
      <section
        className="bg-white border border-slate-200 rounded-lg p-4"
        data-testid="ops-summary-loading"
      >
        <div className="flex items-center gap-2 mb-3">
          <Activity className="w-4 h-4 text-sky-700" />
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Today&apos;s Operational Summary</div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="h-28 bg-slate-50 border border-slate-200 rounded-lg animate-pulse" />
          ))}
        </div>
      </section>
    );
  }

  if (!data) return null;
  const { staff, visits, medication, appointments, incidents, reviews } = data;

  return (
    <section
      className="bg-white border border-slate-200 rounded-lg p-4"
      data-testid="ops-summary"
      aria-label="Today's operational summary"
    >
      <header className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Activity className="w-4 h-4 text-sky-700" />
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Today&apos;s Operational Summary</div>
          <span className="text-[10px] text-slate-400 tabular-nums hidden sm:inline">· {data.date}</span>
        </div>
        <div className="text-[10px] uppercase tracking-widest text-slate-400 hidden sm:flex items-center gap-1">
          10-second overview
        </div>
      </header>

      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3" data-testid="ops-summary-grid">
        {/* Card 1 — Staff on duty */}
        <Card
          to="/staff-schedule"
          icon={Users}
          label="Staff On Duty"
          testid="ops-card-staff"
          status={staff.status}
          headline={<>{staff.on_duty} <span className="text-slate-400 font-medium text-lg">/</span> {staff.total}</>}
          sub={
            <>
              <div className="flex items-center justify-between"><span>Minimum</span><span className="tabular-nums font-medium text-slate-800">{staff.min_staffing}</span></div>
              <div className="flex items-center justify-between"><span>Gap</span><span className={`tabular-nums font-medium ${staff.total - staff.on_duty > 0 ? "text-slate-800" : "text-emerald-700"}`}>{Math.max(0, staff.total - staff.on_duty)}</span></div>
            </>
          }
        />

        {/* Card 2 — Visits today */}
        <Card
          to="/rota"
          icon={Home}
          label="Visits Today"
          testid="ops-card-visits"
          status={visits.status}
          headline={visits.total}
          sub={
            <>
              <div className="flex items-center justify-between"><span>Completed</span><span className="tabular-nums font-medium text-emerald-700">{visits.completed}</span></div>
              <div className="flex items-center justify-between"><span>Remaining</span><span className="tabular-nums font-medium text-slate-800">{visits.remaining}</span></div>
            </>
          }
        />

        {/* Card 3 — Medication rounds */}
        <Card
          to="/mar-chart"
          icon={Pill}
          label="Medication Rounds"
          testid="ops-card-medication"
          status={medication.status}
          headline={<>{medication.given} <span className="text-slate-400 font-medium text-lg">/</span> {medication.scheduled}</>}
          sub={
            <>
              <div className="flex items-center justify-between"><span>Due Soon</span><span className="tabular-nums font-medium text-amber-700">{medication.due_soon}</span></div>
              <div className="flex items-center justify-between"><span>Overdue</span><span className={`tabular-nums font-medium ${medication.overdue > 0 ? "text-red-700" : "text-emerald-700"}`}>{medication.overdue}</span></div>
            </>
          }
        />

        {/* Card 4 — Appointments today */}
        <Card
          to="/appointments"
          icon={CalendarDays}
          label="Appointments Today"
          testid="ops-card-appointments"
          status={appointments.status}
          headline={appointments.total}
          sub={
            <>
              <div className="flex items-center justify-between"><span>Hospital</span><span className="tabular-nums font-medium text-slate-800">{appointments.hospital}</span></div>
              <div className="flex items-center justify-between"><span>GP</span><span className="tabular-nums font-medium text-slate-800">{appointments.gp}</span></div>
              <div className="flex items-center justify-between"><span>Activities</span><span className="tabular-nums font-medium text-slate-800">{appointments.activities}</span></div>
            </>
          }
        />

        {/* Card 5 — Incidents today */}
        <Card
          to="/incidents"
          icon={AlertOctagon}
          label="Incidents Today"
          testid="ops-card-incidents"
          status={incidents.status}
          headline={incidents.open + incidents.closed}
          sub={
            <>
              <div className="flex items-center justify-between"><span>Open</span><span className={`tabular-nums font-medium ${incidents.open > 0 ? "text-red-700" : "text-emerald-700"}`}>{incidents.open}</span></div>
              <div className="flex items-center justify-between"><span>Closed</span><span className="tabular-nums font-medium text-slate-800">{incidents.closed}</span></div>
            </>
          }
        />

        {/* Card 6 — Reviews due */}
        <Card
          to="/care-plans"
          icon={ClipboardList}
          label="Reviews Due"
          testid="ops-card-reviews"
          status={reviews.status}
          headline={reviews.care_plans + reviews.risk_assessments}
          sub={
            <>
              <div className="flex items-center justify-between"><span>Care Plans</span><span className={`tabular-nums font-medium ${reviews.care_plans > 0 ? "text-amber-700" : "text-emerald-700"}`}>{reviews.care_plans}</span></div>
              <div className="flex items-center justify-between"><span>Risk Assessments</span><span className={`tabular-nums font-medium ${reviews.risk_assessments > 0 ? "text-amber-700" : "text-emerald-700"}`}>{reviews.risk_assessments}</span></div>
            </>
          }
        />
      </div>
    </section>
  );
}
